/**
 * WebSocket message processors for charter prep (Phase 1) and
 * group session (Phase 2 & 3).
 *
 * Each processor maps a WS message `type` to a Redux dispatch.
 */
import { toast } from 'sonner'
import store from '@/redux/store'
import type { MessageProcessor } from '@/services/websocketProcessorTypes'

// Prep slice actions
import {
  setChatHistory,
  appendOrUpdateStreamChunk,
  finalizeStreamMessage,
  markSectionComplete,
  setActiveSectionKey,
  setQualityCheckFailed,
  setProfileApprovalRequired,
  setIsGeneratingProfile,
  markProfileApproved,
  setStatus,
  setError as setPrepError,
} from '../redux/slices/charterPrepWsSlice'

// Group slice actions
import {
  setGroupChatHistory,
  appendGroupMessage,
  appendOrUpdateModeratorChunk,
  finalizeModeratorMessage,
  setCurrentStage,
  setConsensusRecord,
  recordRatificationVote,
  setCharterRatified,
  setPendingRenegotiationRequest,
  setGroupError,
  prependGroupMessages,
  setModeratorThinking,
  setRatificationPromptActive,
  setRatificationFinalizing,
  applyRenegotiationVotes,
  setRenegotiationMode,
  setGroupCharterStatus,
} from '../redux/slices/charterGroupWsSlice'

// Charter REST slice actions (for session updates)
import { updateSessionStatus } from '../redux/slices/charterSlice'
import { fetchCharterSession } from '../redux/asyncThunks/charterThunks'

/**
 * Professor drove the room-wide charter Round 0 (start/advance/previous/end/
 * reset). Re-fetch this client's session so the page guards re-navigate
 * (CharterPrepPage / CharterGroupPage react to session.phase / session.status).
 */
const handleCharterRoundUpdate = (data: { action?: string }) => {
  const roomId = store.getState().room.currentRoom?.roomid
  if (roomId) {
    store.dispatch(fetchCharterSession({ roomId }))
  }
  if (data.action === 'reset') {
    toast.info('The instructor reset the team charter. Restarting Phase 1.')
  }
}

// Prep monitor slice actions
import {
  setMonitorConnected,
  setMonitorHistory,
  appendMonitorChunk,
  finalizeMonitorMessage,
  markMonitorSectionComplete,
  markMonitorProfileApproved,
  setMonitorProfile,
  setMonitorError,
} from '../redux/slices/charterPrepMonitorSlice'

// ──────────────────────────────────────────────────────────
// Phase 1 (prep) processors
// ──────────────────────────────────────────────────────────

export const charterPrepProcessors: MessageProcessor[] = [
  {
    type: 'chat_history',
    handler: (data) => {
      store.dispatch(
        setChatHistory({
          messages: data.messages || [],
          completed_sections: data.completed_sections || {},
          active_section_key: data.active_section_key ?? null,
          profile: data.profile ?? null,
          sharing_mode: data.sharing_mode || 'auto',
          visible_sections: data.visible_sections || [],
          section_order: data.section_order || [],
          status: data.status ?? null,
          profile_approved: data.profile_approved ?? false,
        })
      )
    },
  },
  {
    type: 'chat_message_chunk',
    handler: (data) => {
      store.dispatch(
        appendOrUpdateStreamChunk({
          stream_id: data.stream_id,
          content: data.content || '',
        })
      )
    },
  },
  {
    type: 'chat_message_final',
    handler: (data) => {
      store.dispatch(
        finalizeStreamMessage({
          stream_id: data.stream_id,
          content: data.content || '',
          section_key: data.section_key,
        })
      )
    },
  },
  {
    type: 'section_completed',
    handler: (data) => {
      store.dispatch(
        markSectionComplete({
          section_key: data.section_key,
          completed_sections: data.completed_sections || {},
        })
      )
      if (data.active_section_key !== undefined) {
        store.dispatch(setActiveSectionKey(data.active_section_key))
      }
      // If this was the last section, show generating indicator immediately
      const { sectionOrder } = store.getState().charterPrepWS
      const completed = data.completed_sections || {}
      const allDone =
        sectionOrder.length > 0 && sectionOrder.every((k) => k in completed)
      if (allDone) {
        store.dispatch(setIsGeneratingProfile(true))
      }
    },
  },
  {
    type: 'section_incomplete',
    handler: (data) => {
      // One short note per section that replaces rather than stacks: the
      // coach's own reply already asks for what is missing.
      if (data.message) {
        toast.info(data.message, {
          id: `section_incomplete_${data.section_key ?? ''}`,
          duration: 4000,
        })
      }
    },
  },
  {
    type: 'quality_check_failed',
    handler: (data) => {
      store.dispatch(
        setQualityCheckFailed({
          section_key: data.section_key,
          trigger_type: data.trigger_type ?? null,
          coaching_hint: data.coaching_hint ?? null,
        })
      )
    },
  },
  {
    type: 'profile_approval_required',
    handler: (data) => {
      store.dispatch(setIsGeneratingProfile(false))
      store.dispatch(
        setProfileApprovalRequired({
          profile: data.profile || {},
        })
      )
    },
  },
  {
    type: 'phase2_unlocked',
    handler: (data) => {
      toast.success(
        data.message || 'All team members are ready! The group charter session is now open.',
        { duration: 6000 }
      )
      store.dispatch(setStatus('phase2_unlocked'))
      if (data.session_id) {
        store.dispatch(updateSessionStatus({ status: 'in_progress' }))
      }
    },
  },
  {
    type: 'sharing_mode_updated',
    handler: (_data) => {
      // Handled optimistically in UI — no action needed
    },
  },
  {
    type: 'visible_sections_updated',
    handler: (_data) => {
      // Handled optimistically in UI — no action needed
    },
  },
  {
    type: 'profile_approved',
    handler: (data) => {
      store.dispatch(markProfileApproved())
      toast.success(data.message || 'Profile approved!')
    },
  },
  {
    type: 'profile_summary',
    handler: (_data) => {
      // Handled via REST — no action needed here
    },
  },
  {
    type: 'charter_round_update',
    handler: handleCharterRoundUpdate,
  },
  {
    type: 'error',
    handler: (data) => {
      const message = data.message || 'An error occurred.'
      store.dispatch(setPrepError(message))
      // The charter feature simply being off is not an actionable error for
      // players — suppress that toast (but still surface genuine errors).
      if (!/not enabled for this room/i.test(message)) {
        toast.error(data.message || 'An error occurred. Please try again.')
      }
    },
  },
  {
    // Mirrors the group side: the waiting-room processor acts on this, the
    // charter slice needs nothing. Registered so it stops being logged as an
    // unhandled message on every broadcast.
    type: 'charter_status_update',
    handler: () => {},
  },
  {
    // Heartbeat reply and generic notices — nothing for this slice to do, but
    // both arrive routinely and were filling the console with warnings.
    type: 'pong',
    handler: () => {},
  },
  {
    type: 'notification',
    handler: () => {},
  },
]

// ──────────────────────────────────────────────────────────
// Phase 2 & 3 (group) processors
// ──────────────────────────────────────────────────────────

export const charterGroupProcessors: MessageProcessor[] = [
  {
    type: 'chat_history',
    handler: (data) => {
      store.dispatch(
        setGroupChatHistory({
          messages: data.messages || [],
          current_stage: data.current_stage ?? null,
          ratification_votes: data.ratification_votes || {},
          consensus_records: data.consensus_records || [],
          team_name: data.team_name ?? null,
          amendment_mode: data.amendment_mode ?? false,
          charter_status: data.charter_status ?? null,
          charter_version: data.charter_version ?? 1,
          ratified_charter: data.ratified_charter ?? null,
          has_more: data.has_more ?? false,
          total_members: data.total_members,
        })
      )
      // Rehydrate an in-progress amendment vote so reconnecting / late-joining
      // members immediately see the live vote panel.
      store.dispatch(
        setPendingRenegotiationRequest(data.pending_renegotiation_request ?? null)
      )
    },
  },
  {
    type: 'group_message',
    handler: (data) => {
      store.dispatch(
        appendGroupMessage({
          id: data.id || String(Date.now()),
          role: data.role || 'user',
          content: data.content || '',
          sender_display_name: data.sender_display_name || 'Team Member',
          sender_id: data.sender_id ?? null,
          stage: data.stage || 'opening',
          is_intervention: data.is_intervention ?? false,
          intervention_type: data.intervention_type,
          is_streaming: false,
          created_at: data.created_at || new Date().toISOString(),
        })
      )
    },
  },
  {
    type: 'moderator_chunk',
    handler: (data) => {
      store.dispatch(
        appendOrUpdateModeratorChunk({
          stream_id: data.stream_id || String(Date.now()),
          content: data.content || '',
          is_first: data.is_first ?? false,
        })
      )
    },
  },
  {
    type: 'moderator_final',
    handler: (data) => {
      store.dispatch(
        finalizeModeratorMessage({
          stream_id: data.stream_id,
          id: data.id || `mod_${Date.now()}`,
          content: data.content || '',
          stage: data.stage || 'opening',
          is_intervention: data.is_intervention ?? false,
          intervention_type: data.intervention_type,
          created_at: data.created_at || new Date().toISOString(),
        })
      )
    },
  },
  {
    type: 'moderator_thinking',
    handler: () => {
      store.dispatch(setModeratorThinking(true))
    },
  },
  {
    type: 'stage_advanced',
    handler: (data) => {
      store.dispatch(setCurrentStage(data.to_stage))
      toast.success(`Moving to: ${_formatStageName(data.to_stage)}`, { duration: 3000 })
    },
  },
  {
    type: 'consensus_confirmed',
    handler: (data) => {
      store.dispatch(
        setConsensusRecord({
          section_key: data.section_key,
          consensus_text: data.consensus_text || '',
          confirmed_by: data.confirmed_by,
        })
      )
      toast.success(`Consensus reached on: ${_formatSectionName(data.section_key)}`, {
        duration: 3000,
      })
    },
  },
  {
    type: 'ratification_update',
    handler: (data) => {
      store.dispatch(
        recordRatificationVote({
          votes: data.votes || {},
          total_members: data.total_members || 0,
          yes_count: data.yes_count || 0,
        })
      )
    },
  },
  {
    type: 'ratification_prompt',
    handler: (_data) => {
      store.dispatch(setRatificationPromptActive(true))
      toast.info('Your charter is ready for final ratification!', { duration: 5000 })
    },
  },
  {
    type: 'ratification_finalizing',
    handler: () => {
      store.dispatch(setRatificationFinalizing(true))
    },
  },
  {
    type: 'ratification_concern',
    handler: (data) => {
      // A NO vote pauses the vote. Close the panel and drop the tally, or the
      // input stays locked on "Please vote" and the member who raised the
      // concern can never vote again.
      store.dispatch(setRatificationFinalizing(false))
      store.dispatch(setRatificationPromptActive(false))
      store.dispatch(
        recordRatificationVote({
          votes: {},
          total_members: store.getState().charterGroupWS.totalMembers,
          yes_count: 0,
        })
      )
      toast.warning(
        `${data.voter_name} raised a concern${data.section_key ? ` on ${_formatSectionName(data.section_key)}` : ''}. Re-opening discussion.`,
        { duration: 5000 }
      )
    },
  },
  {
    type: 'charter_ratified',
    handler: (data) => {
      store.dispatch(
        setCharterRatified({
          charter: data.charter,
          version: data.version || 1,
        })
      )
      store.dispatch(updateSessionStatus({ status: 'locked', version: data.version }))
      store.dispatch(setRatificationPromptActive(false))
      store.dispatch(setRatificationFinalizing(false))
      toast.success('🎉 Your team charter has been ratified!', { duration: 8000 })
    },
  },
  {
    type: 'more_messages',
    handler: (data) => {
      store.dispatch(
        prependGroupMessages({
          messages: data.messages || [],
          has_more: data.has_more ?? false,
        })
      )
    },
  },
  {
    type: 'easy_win_objection',
    handler: (data) => {
      toast.info(
        `${data.objector_name} raised an objection on "${_formatSectionName(data.section_key)}". Moving to discussion.`,
        { duration: 4000 }
      )
    },
  },
  {
    type: 'renegotiation_proposed',
    handler: (data) => {
      store.dispatch(setPendingRenegotiationRequest(data.request ?? null))
      toast.info('A charter amendment has been proposed. Please vote.', { duration: 6000 })
    },
  },
  {
    type: 'renegotiation_vote_update',
    handler: (data) => {
      // Reflect every vote live (including the final one) before handling the outcome.
      if (data.votes) {
        store.dispatch(applyRenegotiationVotes(data.votes))
      }
      if (data.outcome === 'rejected') {
        toast.error('Amendment request rejected. The current charter remains in effect.')
        store.dispatch(setPendingRenegotiationRequest(null))
      } else if (data.outcome === 'approved') {
        toast.success('Amendment approved! Opening re-negotiation session.', { duration: 5000 })
      }
    },
  },
  {
    type: 'renegotiation_approved',
    handler: (_data) => {
      // Amendment approved (sent by the backend enter_amendment flow, which also
      // flips the connected consumer into amendment mode and streams the opening
      // on the same socket). Update the UI directly — no reconnect: close the
      // vote panel and set 'renegotiating' so the chat input re-enables. Update
      // BOTH the WS-slice charterStatus and the REST session.status, since the
      // input's `isLocked` reads whichever is 'locked'.
      store.dispatch(setPendingRenegotiationRequest(null))
      store.dispatch(setGroupCharterStatus({ status: 'renegotiating' }))
      store.dispatch(updateSessionStatus({ status: 'renegotiating' }))
      store.dispatch(setRenegotiationMode(true))
    },
  },
  {
    type: 'renegotiation_rejected',
    handler: (data) => {
      toast.error(data.reason || 'Amendment was rejected.')
      store.dispatch(setPendingRenegotiationRequest(null))
    },
  },
  {
    type: 'renegotiation_cancelled',
    handler: (data) => {
      // The amendment was abandoned and the session reverted to the last
      // locked charter. Flip the WS-driven view back to "locked" (the REST
      // session refresh alone wouldn't update the WS slice the view reads).
      store.dispatch(setRenegotiationMode(false))
      store.dispatch(
        setGroupCharterStatus({ status: 'locked', version: data.version })
      )
      // Also flip the REST session.status immediately (the input's isLocked reads
      // it) so the chat reverts to reference-only without waiting for the refetch.
      store.dispatch(updateSessionStatus({ status: 'locked' }))
      store.dispatch(setRatificationFinalizing(false))
      store.dispatch(setRatificationPromptActive(false))
      store.dispatch(setPendingRenegotiationRequest(null))
      const roomId = store.getState().room.currentRoom?.roomid
      if (roomId) {
        store.dispatch(fetchCharterSession({ roomId }))
      }
      toast.info('Amendment cancelled — reverted to the locked charter.', {
        duration: 5000,
      })
    },
  },
  {
    type: 'charter_updated',
    handler: (data) => {
      store.dispatch(
        setCharterRatified({
          charter: data.charter,
          version: data.version,
        })
      )
      store.dispatch(updateSessionStatus({ status: 'locked', version: data.version }))
      toast.success(`Charter updated to Version ${data.version}!`, { duration: 5000 })
    },
  },
  {
    type: 'charter_status_update',
    handler: (_data) => {
      // Propagated to waiting room processor as well — no charter slice action needed
    },
  },
  {
    type: 'charter_round_update',
    handler: handleCharterRoundUpdate,
  },
  {
    type: 'error',
    handler: (data) => {
      const message = data.message || 'An error occurred.'
      store.dispatch(setGroupError(message))
      // If finalization failed, drop the "in process" banner so the team isn't stuck.
      store.dispatch(setRatificationFinalizing(false))
      // The charter feature simply being off is not an actionable error for
      // players — suppress that toast (but still surface genuine errors).
      if (!/not enabled for this room/i.test(message)) {
        toast.error(data.message || 'An error occurred.')
      }
    },
  },
]

// ──────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────

function _formatStageName(stage: string): string {
  const names: Record<string, string> = {
    opening: 'Opening',
    easy_wins: 'Easy Wins',
    conflicts: 'Conflict Resolution',
    group_sections: 'Group Sections',
    gap_review: 'Gap Review',
  }
  return names[stage] || stage
}

function _formatSectionName(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

// ──────────────────────────────────────────────────────────
// Phase 1 monitor processors (professor observing a student's prep)
// ──────────────────────────────────────────────────────────

// connectionId pattern: 'charter_prep_monitor_{memberId}'
function _extractMemberId(connectionId: string): string {
  return connectionId.replace('charter_prep_monitor_', '')
}

export function buildCharterPrepMonitorProcessors(
  connectionId: string
): import('@/services/websocketProcessorTypes').MessageProcessor[] {
  const memberId = _extractMemberId(connectionId)
  return [
    {
      type: 'chat_history',
      handler: (data) => {
        store.dispatch(
          setMonitorHistory({
            memberId,
            messages: data.messages || [],
            completed_sections: data.completed_sections || {},
            active_section_key: data.active_section_key ?? null,
            profile: data.profile ?? null,
            sharing_mode: data.sharing_mode || 'auto',
            visible_sections: data.visible_sections || [],
            section_order: data.section_order || [],
            profile_approved: data.profile_approved ?? false,
          })
        )
        store.dispatch(setMonitorConnected({ memberId, connected: true }))
      },
    },
    {
      type: 'chat_message_chunk',
      handler: (data) => {
        store.dispatch(
          appendMonitorChunk({
            memberId,
            stream_id: data.stream_id,
            content: data.content || '',
          })
        )
      },
    },
    {
      type: 'chat_message_final',
      handler: (data) => {
        store.dispatch(
          finalizeMonitorMessage({
            memberId,
            stream_id: data.stream_id,
            content: data.content || '',
            section_key: data.section_key,
          })
        )
      },
    },
    {
      type: 'section_completed',
      handler: (data) => {
        store.dispatch(
          markMonitorSectionComplete({
            memberId,
            section_key: data.section_key,
            completed_sections: data.completed_sections || {},
            active_section_key: data.active_section_key ?? null,
          })
        )
      },
    },
    {
      type: 'profile_approval_required',
      handler: (data) => {
        store.dispatch(setMonitorProfile({ memberId, profile: data.profile || {} }))
      },
    },
    {
      type: 'profile_approved',
      handler: () => {
        store.dispatch(markMonitorProfileApproved(memberId))
      },
    },
    {
      type: 'error',
      handler: (data) => {
        store.dispatch(
          setMonitorError({ memberId, error: data.message || 'Connection error' })
        )
      },
    },
  ]
}
